import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  FaArrowRight, 
  FaBriefcase, 
  FaBuilding, 
  FaFileAlt, 
  FaLightbulb,
  FaSearch,
  FaPaste,
  FaExclamationTriangle
} from 'react-icons/fa';
import './JobDescriptionInput.css';

const JobDescriptionInput = ({ jobDescription, jobTitle, company, onChange, onNext }) => {
  const [errors, setErrors] = useState({
    jobDescription: '',
  });
  const [charCount, setCharCount] = useState(0);
  const [showTips, setShowTips] = useState(false);
  const [pasting, setPasting] = useState(false);

  // Validate form before proceeding
  const validateForm = () => {
    const newErrors = {};

    if (!jobDescription.trim()) {
      newErrors.jobDescription = 'Job description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next button click
  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  // Handle text area change and update character count
  const handleTextAreaChange = (e) => {
    onChange(e);
    setCharCount(e.target.value.length);
  };

  // Handle paste animation
  const handlePaste = () => {
    setPasting(true);
    setTimeout(() => setPasting(false), 1000);
  };

  // Toggle tips visibility
  const toggleTips = () => {
    setShowTips(!showTips);
  };

  // Example job keywords
  const jobKeywords = ['experience', 'skills', 'qualifications', 'requirements', 'responsibilities'];

  return (
    <div className="job-description-input">
      <div className="section-description">
        <h2> Enter Job Description</h2>
        <p>
          Enter the job description to tailor your resume. The more detailed the job description, 
          the better we can optimize your resume for this specific opportunity.
        </p>
      </div>

      <div className="form-container">
        <div className="input-row">
          <div className="form-group">
            <label htmlFor="jobTitle">
              <FaBriefcase className="input-icon" /> Job Title <span className="optional-label">(Optional)</span>
            </label>
            <div className="input-wrapper">
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={jobTitle}
                onChange={onChange}
                placeholder="e.g., Senior Software Engineer"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="company">
              <FaBuilding className="input-icon" /> Company <span className="optional-label">(Optional)</span>
            </label>
            <div className="input-wrapper">
              <input
                type="text"
                id="company"
                name="company"
                value={company}
                onChange={onChange}
                placeholder="e.g., Acme Corporation"
              />
            </div>
          </div>
        </div>

        <div className="form-group description-group">
          <div className="label-row">
            <label htmlFor="jobDescription">
              <FaFileAlt className="input-icon" /> Job Description <span className="required-label">*</span>
            </label>
            <button type="button" className="tips-toggle" onClick={toggleTips}>
              <FaLightbulb /> Tips
            </button>
          </div>
          
          <div className="textarea-container">
            <textarea
              id="jobDescription"
              name="jobDescription"
              value={jobDescription}
              onChange={handleTextAreaChange}
              onPaste={handlePaste}
              rows="12"
              placeholder="Paste the full job description here..."
              className={`${errors.jobDescription ? 'error' : ''} ${pasting ? 'pasting' : ''}`}
            />
            <div className="textarea-overlay">
              {!jobDescription && (
                <div className="placeholder-content">
                  <FaPaste className="placeholder-icon" />
                  <p>Paste job description here</p>
                </div>
              )}
            </div>
            
            <div className="textarea-footer">
              <div className="char-count">{charCount} characters</div>
              {jobDescription && (
                <div className="keyword-highlights">
                  {jobKeywords.filter(keyword => 
                    jobDescription.toLowerCase().includes(keyword.toLowerCase())
                  ).map(keyword => (
                    <span key={keyword} className="keyword-tag">
                      <FaSearch size={10} /> {keyword}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {errors.jobDescription && (
            <div className="error-message">
              <FaExclamationTriangle /> {errors.jobDescription}
            </div>
          )}
          
          <div className="small-hint">
            Copy and paste the entire job posting, including requirements, responsibilities, and qualifications.
          </div>
        </div>

        {showTips && (
          <div className="tips-panel">
            <h3><FaLightbulb /> Tips for Better Results</h3>
            <ul>
              <li>Include the <strong>complete</strong> job description for best matching</li>
              <li>Don't edit or summarize - our AI works best with the original text</li>
              <li>Make sure to include sections about required skills and qualifications</li>
              <li>If available, include information about company culture and values</li>
            </ul>
          </div>
        )}

        <div className="form-actions">
          <button onClick={handleNext} className="btn-primary">
            Select Profile Elements <FaArrowRight />
          </button>
        </div>
      </div>
    </div>
  );
};

JobDescriptionInput.propTypes = {
  jobDescription: PropTypes.string.isRequired,
  jobTitle: PropTypes.string,
  company: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default JobDescriptionInput;